import { isErrorWithMessage, isRequest } from './types.js';
import { debug } from './logging.js';
export class EtagCache {
    constructor() {
        Object.defineProperty(this, "cache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
    }
    setValue(url, etag, value) {
        this.cache.set(url, {
            etag,
            value,
            cachedAt: Date.now(),
        });
    }
    getValue(url) {
        return this.cache.get(url);
    }
    clearCahe() {
        this.cache.clear();
    }
}
export function parseUrl(input) {
    if (typeof input === 'string') {
        return input;
    }
    else if (isRequest(input)) {
        return input.url;
    }
    else {
        return input.toString();
    }
}
export const HEADER_IF_NONE_MATCH = 'If-None-Match';
export const HEADER_ETAG = 'ETag';
/**
 * Returns a fetch function that caches responses for a minimum amount of time.
 * Then it follows the HTTP etag caching rules.
 *
 * @param fetch
 * @param cache
 * @param minCacheTimeInSeconds
 * @param transformFunction Function that transforms the response into the desired type, used for improved performance to avoid parsing the response multiple times
 * @param debugOptions
 */
export function cachedFetch(fetch, cache, minCacheTimeInSeconds, transformFunction) {
    return async (input, init) => {
        const url = parseUrl(input);
        const cachedValue = cache.getValue(url);
        const enrichedInit = init ?? {};
        if (cachedValue) {
            const { etag, value, cachedAt } = cachedValue;
            const now = Date.now();
            const cacheAgeInSeconds = (now - cachedAt) / 1000;
            if (cacheAgeInSeconds < minCacheTimeInSeconds) {
                debug(`Using cached value for ${url} because it is not older than ${minCacheTimeInSeconds} seconds`);
                return value;
            }
            if (etag) {
                enrichedInit.headers = {
                    ...enrichedInit.headers,
                    [HEADER_IF_NONE_MATCH]: etag,
                };
            }
            debug(`Going to fetch new data for previously cached with etag ${etag}.`);
        }
        else {
            debug(`Going to fetch new data because it is not cached yet.`);
        }
        try {
            const response = await fetch(input, init);
            const etag = response.headers.get(HEADER_ETAG);
            if ((response.status === 304 || !response.ok) && cachedValue) {
                debug(`Using cached value for url: ${url}, etag: ${etag} because the server returned a ${response.status === 304 ? 304 : 'an error'}.`);
                cachedValue.cachedAt = Date.now();
                return cachedValue.value;
            }
            else if (response.ok) {
                const newValue = await transformFunction(response);
                debug(`Using new value for ur: ${url}, etag: ${etag} because the server returned a new value. ${JSON.stringify(newValue)}`);
                cache.setValue(url, etag, newValue);
                return newValue;
            }
            else {
                debug(`The server returned an error for url: ${url}, etag: ${etag}.`);
                return await transformFunction(response);
            }
        }
        catch (e) {
            if (cachedValue) {
                console.warn(`Failed to fetch new data for url: ${url}, etag: ${cachedValue.etag}. Using cached value. Error: ${isErrorWithMessage(e) ? e.message : ''}`);
                return cachedValue.value;
            }
            else {
                throw e;
            }
        }
    };
}
